import React, { useEffect, useState, useCallback, useMemo } from "react";
import "../styles/SATRequest.css";
import "../styles/AdminApplications.css";
import { apiClient } from "../services/apiClient";
import { useConfirm } from "../providers/ConfirmProvider";
import { useI18n } from "../providers/I18nProvider";
import { TabBar, FilterBar, SearchInput, EmptyState, countBy } from "../components/ListToolbar";

// Başvuru tipleri (Tam Zamanlı, Staj vb.) — admin CRUD.
// Soft delete: silinen tip active=false yapılır (BTO ilanlarında stale id
// olmaması için).

const emptyForm = { name_tr: "", name_en: "", active: true, sort_order: 0 };

const AdminApplicationTypesPage = () => {
  const confirm = useConfirm();
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("active");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get("/application-types");
      setRows(res.data || []);
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("applications.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const tabCounts = useMemo(
    () => countBy(rows, { active: (r) => r.active, inactive: (r) => !r.active, all: () => true }),
    [rows]
  );
  const visibleRows = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    return rows.filter((r) => {
      if (tab === "active" && !r.active) return false;
      if (tab === "inactive" && r.active) return false;
      if (!q) return true;
      return `${r.name_tr || ""} ${r.name_en || ""}`.toLocaleLowerCase("tr-TR").includes(q);
    });
  }, [rows, tab, search]);

  const startEdit = (row) => {
    setEditingId(row.id);
    window.scrollTo?.({ top: 0, behavior: "smooth" });
    setForm({
      name_tr: row.name_tr || "",
      name_en: row.name_en || "",
      active: !!row.active,
      sort_order: row.sort_order || 0,
    });
  };

  const cancel = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const save = async () => {
    if (!form.name_tr.trim() || !form.name_en.trim()) {
      setError(t("applications.types.namesRequired"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (editingId) {
        await apiClient.put(`/application-types/${editingId}`, form);
      } else {
        await apiClient.post("/application-types", form);
      }
      cancel();
      await load();
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("applications.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!(await confirm(t("applications.types.deactivateConfirm", { name: row.name_tr })))) return;
    setBusy(true);
    setError("");
    try {
      await apiClient.delete(`/application-types/${row.id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.msg || err.message || t("applications.deleteFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sat-page apps-meta-page">
      <div className="sat-page-header">
        <div>
          <h2>{t("applications.types.title")}</h2>
          <div className="subtitle">{t("applications.types.subtitle")}</div>
        </div>
      </div>

      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}

      <form
        className="apps-form-card"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <h3>{editingId ? t("applications.editTitle", { id: editingId }) : t("applications.types.addTitle")}</h3>
        <div className="apps-form-grid apps-form-grid--types">
          <label>
            {t("applications.types.nameTr")}
            <input
              type="text"
              className="apps-input"
              value={form.name_tr}
              onChange={(e) => setForm({ ...form, name_tr: e.target.value })}
            />
          </label>
          <label>
            {t("applications.types.nameEn")}
            <input
              type="text"
              className="apps-input"
              value={form.name_en}
              onChange={(e) => setForm({ ...form, name_en: e.target.value })}
            />
          </label>
          <label>
            {t("applications.types.order")}
            <input
              type="number"
              className="apps-input"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
            />
          </label>
          <label className="apps-check">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            {t("applications.types.active")}
          </label>
          <div className="apps-form-actions">
            <button type="submit" disabled={busy} className="sat-btn sat-btn--primary">
              {editingId ? t("applications.update") : t("applications.add")}
            </button>
            {editingId && (
              <button type="button" onClick={cancel} className="sat-btn">
                {t("applications.cancel")}
              </button>
            )}
          </div>
        </div>
      </form>

      <TabBar
        value={tab}
        onChange={setTab}
        ariaLabel={t("applications.types.title")}
        tabs={[
          { key: "active", label: t("applications.tabActive"), count: tabCounts.active },
          { key: "inactive", label: t("applications.tabInactive"), count: tabCounts.inactive },
          { key: "all", label: t("applications.tabAll"), count: tabCounts.all },
        ]}
      />
      <FilterBar showReset={Boolean(search)} onReset={() => setSearch("")}>
        <SearchInput value={search} onChange={setSearch} placeholder={t("applications.types.searchPlaceholder")} />
      </FilterBar>

      {loading && <div className="sat-empty">{t("applications.loading")}</div>}
      {!loading && visibleRows.length === 0 && (
        <EmptyState message={rows.length === 0 ? t("applications.empty") : t("applications.emptyFilteredRecords")} />
      )}
      {!loading && visibleRows.length > 0 && (
        <table className="sat-table apps-meta-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t("applications.types.colTr")}</th>
              <th>{t("applications.types.colEn")}</th>
              <th>{t("applications.types.order")}</th>
              <th>{t("applications.types.active")}</th>
              <th>{t("applications.columns.action")}</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => (
              <tr key={r.id} className={r.active ? "" : "apps-row--inactive"}>
                <td>{r.id}</td>
                <td>{r.name_tr}</td>
                <td>{r.name_en}</td>
                <td>{r.sort_order}</td>
                <td>{r.active ? t("applications.yes") : t("applications.no")}</td>
                <td>
                  <div className="apps-row-actions">
                    <button type="button" onClick={() => startEdit(r)} className="sat-btn">
                      {t("applications.edit")}
                    </button>
                    {r.active && (
                      <button type="button" onClick={() => remove(r)} disabled={busy} className="sat-btn sat-btn--danger">
                        {t("applications.types.deactivate")}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminApplicationTypesPage;
